import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import clsx from "clsx";
import Cookies from "js-cookie";
import { Building2, Check, FileText, MessageSquare, X } from "lucide-react";
import { TailSpin } from "react-loader-spinner";

import { GET_WFH_TIMESHEETS } from "../../../../../../../../config/Endpoints.js";
import { displayFormatDate } from "../../../../../../../../utils/helpers.js";
import { getApi } from "../../../../../../../utils/api.ts";
import { Modal, Button, Badge, ResponsiveDataTable } from "../../../../../../ui/index.ts";
import { DatePicker } from "../../../../../../ui/date-picker/date-picker.tsx";

import styles from "./view-timesheets-modal.module.css";

/* ─── Types ─── */

interface TimesheetEntry {
  titleType: string;
  projectId: number | null;
  titleName: string | null;
  description: string;
  duration: number;
  taskStatus: string;
  projectName: string | null;
  phase?: "DISCOVERY" | "SOLUTION_ARCHITECTURE" | "DEVELOPMENT" | "UAT" | null;
  approverId?: number | null;
  approverType?: string | null;
}

interface TimesheetApproval {
  approverId: number;
  approverName: string;
  approverType: string;
  status: string;
  comment: string | null;
  actedAt: string | null;
  projectId: number | null;
}

interface TimesheetRecord {
  id: number;
  employeeId: number;
  employeeName: string;
  date: string;
  isSubmitted: number;
  entries: TimesheetEntry[];
  managerStatus: string;
  totalDurationMinutes: number;
  approvals?: TimesheetApproval[];
  timesheetFound?: boolean;
}

interface EntryGroup {
  key: string;
  label: string;
  isMisc: boolean;
  entries: TimesheetEntry[];
  approverId?: number | null;
  approverType?: string | null;
  projectId: number | null;
}

export interface ViewTimesheetsModalProps {
  open: boolean;
  onClose: () => void;
  employeeId?: string;
}

/* ─── Date helpers ─── */

function getYesterday(): Date {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function stripTime(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

const formatTimesheetHeaderDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/* ─── Formatters ─── */

function fmtDuration(minutes: number): string {
  const m = Number(minutes) || 0;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}m`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}m`;
}

function fmtHours(minutes: number): string {
  return fmtDuration(minutes);
}

function fmtTaskStatus(status: string | undefined): string {
  if (status === "COMPLETED") return "Completed";
  if (status === "INPROGRESS") return "In-Progress";
  return status ?? "—";
}

function fmtEntryTitle(title: string | null | undefined): string {
  const normalized = String(title ?? "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ");
  if (!normalized) return "—";
  return normalized
    .split(" ")
    .map((w) =>
      w.length <= 1 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

/* ─── Grouping & approval ─── */

function groupTimesheetEntries(entries: TimesheetEntry[]): EntryGroup[] {
  if (!entries?.length) return [];
  const map = new Map<string, EntryGroup>();

  for (const entry of entries) {
    const isMisc = entry.titleType === "OTHERS";
    const key = isMisc
      ? "others"
      : entry.projectId != null
        ? `project-${entry.projectId}`
        : `project-${String(entry.projectName || "unnamed").replace(/\s+/g, "-")}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        label: isMisc ? "Miscellaneous" : entry.projectName || "Project",
        isMisc,
        entries: [],
        approverId: entry.approverId,
        approverType: entry.approverType,
        projectId: entry.projectId ?? null,
      });
    }
    map.get(key)!.entries.push(entry);
  }
  return Array.from(map.values());
}

function getGroupApproval(
  group: EntryGroup,
  approvals: TimesheetApproval[] | undefined,
): TimesheetApproval | null {
  if (!approvals?.length) return null;

  const forProject = approvals.filter(
    (a) => (a.projectId ?? null) === (group.projectId ?? null),
  );
  if (!forProject.length) return null;

  const decided = forProject.filter(
    (a) => a.status === "APPROVED" || a.status === "REJECTED",
  );
  if (decided.length) {
    return decided.reduce((a, b) =>
      (b.actedAt || "") > (a.actedAt || "") ? b : a,
    );
  }

  return (
    forProject.find((a) => a.approverType === group.approverType) ??
    forProject[0]
  );
}

function badgeVariant(status: string): "success" | "danger" | "neutral" | "warning" {
  const st = String(status).toUpperCase();
  if (st === "APPROVED") return "success";
  if (st === "REJECTED") return "danger";
  if (st === "SKIPPED") return "neutral";
  return "warning";
}

function badgeLabel(status: string): string {
  const st = String(status).toUpperCase();
  if (st === "APPROVED") return "Approved";
  if (st === "REJECTED") return "Rejected";
  if (st === "SKIPPED") return "Skipped";
  return "Pending";
}

function statusAccentClass(status: string): string {
  const st = String(status).toUpperCase();
  if (st === "APPROVED") return styles.groupCardApproved;
  if (st === "REJECTED") return styles.groupCardRejected;
  if (st === "SKIPPED") return styles.groupCardSkipped;
  return styles.groupCardPending;
}

/* ─── Table columns ─── */

const TABLE_COLUMNS = [
  {
    id: "phase",
    header: "Phase",
    accessor: "phase",
    sortable: true,
    getTitleText: (row: { phase: string }) => String(row.phase ?? ""),
    render: (row: { phase: string }) => <span>{fmtEntryTitle(row.phase)}</span>,
  },
  {
    id: "category",
    header: "Category",
    accessor: "category",
    sortable: true,
    getTitleText: (row: { category: string }) => String(row.category ?? ""),
    render: (row: { category: string }) => (
      <span className={styles.categoryBadge}>{row.category}</span>
    ),
  },
  {
    id: "description",
    header: "Task",
    accessor: "description",
    sortable: true,
    getTitleText: (row: { description: string }) => String(row.description ?? ""),
  },
  {
    id: "taskStatus",
    header: "Task Status",
    accessor: "taskStatus",
    sortable: true,
  },
  {
    id: "timeLabel",
    header: "Time",
    accessor: "timeLabel",
    sortable: true,
    nowrap: true,
    render: (row: { timeLabel: string }) => (
      <span className={styles.timeCell}>{row.timeLabel}</span>
    ),
  },
];

/* ─── Date trigger ─── */

type TimesheetDateTriggerProps = React.ComponentPropsWithoutRef<"button"> & {
  selectedDate: Date;
};

const TimesheetDateTrigger = forwardRef<HTMLButtonElement, TimesheetDateTriggerProps>(
  function TimesheetDateTrigger({ selectedDate, className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={clsx(styles.dateTrigger, className)}
        {...rest}
        type="button"
      >
        {formatTimesheetHeaderDate(selectedDate)}
      </button>
    );
  },
);

TimesheetDateTrigger.displayName = "TimesheetDateTrigger";

/* ─── Component ─── */

export function ViewTimesheetsModal({
  open,
  onClose,
  employeeId,
}: ViewTimesheetsModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(getYesterday);
  const [timesheet, setTimesheet] = useState<TimesheetRecord | null>(null);
  const [loading, setLoading] = useState(false);

  const accessToken = Cookies.get("accessToken") ?? "";
  const today = stripTime(new Date());
  const atToday = stripTime(selectedDate).getTime() === today.getTime();
  const selectedDateKey = selectedDate.toLocaleDateString("en-CA");

  const handleClose = () => {
    setSelectedDate(getYesterday());
    setTimesheet(null);
    onClose();
  };

  useEffect(() => {
    if (!open || !employeeId) {
      setTimesheet(null);
      return;
    }

    let isMounted = true;
    const formattedDate = selectedDateKey;

    async function fetchTimesheet() {
      setLoading(true);
      try {
        const response = await getApi<TimesheetRecord[]>(
          `${GET_WFH_TIMESHEETS}?date=${formattedDate}&employeeId=${employeeId}`,
          accessToken,
        );

        if (!isMounted) return;

        const record = response.data?.[0] ?? null;
        if (
          record &&
          record.timesheetFound !== false &&
          (record.entries?.length > 0 || record.isSubmitted === 1)
        ) {
          setTimesheet(record);
        } else {
          setTimesheet(null);
        }
      } catch {
        if (isMounted) setTimesheet(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void fetchTimesheet();
    return () => {
      isMounted = false;
    };
  }, [open, employeeId, selectedDateKey, accessToken]);

  const projectGroups = useMemo(
    () => groupTimesheetEntries(timesheet?.entries ?? []),
    [timesheet?.entries],
  );

  const totalDurationMinutes =
    timesheet?.totalDurationMinutes ??
    (timesheet?.entries ?? []).reduce(
      (t, e) => t + (Number(e.duration) || 0),
      0,
    );

  const modalTitle = timesheet?.employeeName
    ? `${timesheet.employeeName}'s Timesheet`
    : "View Timesheets";

  const subtitleText = timesheet?.date
    ? `Date: ${displayFormatDate(timesheet.date)} · ${fmtHours(totalDurationMinutes)} total`
    : `Date: ${displayFormatDate(selectedDate.toLocaleDateString("en-CA"))}`;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      size="xl"
      headerClassName={styles.modalHeader}
      bodyClassName={styles.modalBody}
      footer={
        <Button variant="muted-outline" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div className={styles.shell}>
        <div className={styles.topBar}>
          <p className={styles.subtitle}>{subtitleText}</p>
          <div className={styles.dateNav}>
            <button
              type="button"
              className={styles.arrowBtn}
              aria-label="Previous day"
              onClick={() => setSelectedDate(addDays(selectedDate, -1))}
              disabled={!employeeId || loading}
            >
              &#8249;
            </button>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              maxDate={new Date()}
              disabled={!employeeId || loading}
              customInput={<TimesheetDateTrigger selectedDate={selectedDate} />}
            />
            <button
              type="button"
              className={styles.arrowBtn}
              aria-label="Next day"
              onClick={() => setSelectedDate(addDays(selectedDate, 1))}
              disabled={!employeeId || loading || atToday}
            >
              &#8250;
            </button>
          </div>
        </div>

        <div className={styles.scrollContent}>
        {!employeeId ? (
          <p className={styles.emptyState}>Please select an employee to view timesheets.</p>
        ) : loading ? (
          <div className={styles.loadingWrap} role="status" aria-live="polite">
            <TailSpin
              height="60"
              width="60"
              color="var(--primary-color)"
              visible
            />
            <p className={styles.loadingText}>Loading timesheet…</p>
          </div>
        ) : !timesheet || projectGroups.length === 0 ? (
          <p className={styles.emptyState}>No records available</p>
        ) : (
          <div className={styles.groups}>
            {projectGroups.map((group) => {
              const approval = getGroupApproval(group, timesheet.approvals);
              const approvalStatus = approval?.status || "PENDING";
              const groupMinutes = group.entries.reduce(
                (t, e) => t + (Number(e.duration) || 0),
                0,
              );

              const rows = group.entries.map((entry, idx) => ({
                id: `${group.key}-${idx}`,
                category: fmtEntryTitle(entry.titleName),
                phase: fmtEntryTitle(entry.phase),
                description: entry.description || "—",
                taskStatus: fmtTaskStatus(entry.taskStatus),
                timeLabel: fmtDuration(entry.duration),
              }));

              const isDecided =
                approvalStatus === "APPROVED" || approvalStatus === "REJECTED";

              return (
                <div
                  key={group.key}
                  className={clsx(styles.groupCard, statusAccentClass(approvalStatus))}
                >
                  <div className={styles.groupHeader}>
                    <div className={styles.groupIcon}>
                      {group.isMisc ? (
                        <FileText size={20} />
                      ) : (
                        <Building2 size={20} />
                      )}
                    </div>
                    <div className={styles.groupTitleBlock}>
                      <h4 className={styles.groupName}>{group.label}</h4>
                      <p className={styles.groupSubtitle}>
                        {group.isMisc
                          ? `${approval?.approverType || "RM"}: ${approval?.approverName || "—"}`
                          : `${approval?.approverType || group.approverType}: ${approval?.approverName || "—"}`}
                      </p>
                    </div>
                    <div className={styles.groupStats}>
                      <Badge variant={badgeVariant(approvalStatus)}>
                        {badgeLabel(approvalStatus)}
                      </Badge>
                      <span className={styles.groupHours}>{fmtHours(groupMinutes)}</span>
                    </div>
                  </div>

                  {isDecided && approval && (
                    <div className={styles.actionHistory}>
                      <div className={styles.historyLine}>
                        <span
                          className={clsx(
                            styles.historyIcon,
                            approvalStatus === "APPROVED"
                              ? styles.historyIconApproved
                              : styles.historyIconRejected,
                          )}
                        >
                          {approvalStatus === "APPROVED" ? (
                            <Check size={14} />
                          ) : (
                            <X size={14} />
                          )}
                        </span>
                        <span>
                          {badgeLabel(approvalStatus)} by{" "}
                          {approval.approverName || "—"} (
                          {approval.approverType || "—"})
                          {approval.actedAt
                            ? ` on ${fmtDateTime(approval.actedAt)}`
                            : ""}
                        </span>
                      </div>
                      {approval.comment && (
                        <div className={styles.commentLine}>
                          <MessageSquare size={14} className={styles.commentIcon} />
                          <span>{approval.comment}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles.entriesSection}>
                    <p className={styles.entriesLabel}>
                      Time Entries ({group.entries.length})
                    </p>
                    <ResponsiveDataTable
                      pagination={false}
                      columns={TABLE_COLUMNS}
                      data={rows}
                      aria-label={`Tasks for ${group.label}`}
                      emptyState={
                        <span className={styles.emptyState}>No entries</span>
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </Modal>
  );
}

export default ViewTimesheetsModal;
